import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const footerLinks = {
  company: [
    { name: "About", href: "/about" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ],
  solutions: [
    { name: "Products", href: "/products" },
    { name: "Studio", href: "/studio" },
    { name: "Case Studies", href: "/case-studies" },
  ],
  resources: [
    { name: "Blog", href: "/blog" },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter`, { email });
      toast.success("Subscribed successfully");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("Already subscribed");
      } else {
        toast.error("Failed to subscribe");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-obsidian border-t border-white/5" data-testid="footer">
      {/* Large CTA Section */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-5xl md:text-7xl font-heading font-bold text-white leading-none tracking-tight">
              Let's build
              <br />
              <span className="text-mint">something.</span>
            </h2>
          </div>
          <div className="flex justify-start lg:justify-end">
            <Link to="/contact">
              <button 
                data-testid="footer-cta-btn"
                className="group flex items-center gap-4 bg-mint text-black px-10 py-6 font-bold text-lg tracking-wide uppercase hover:bg-white transition-colors duration-300"
              >
                Start a Project
                <ArrowUpRight className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
            {/* Brand & Newsletter */}
            <div className="lg:col-span-5">
              <Link to="/" className="inline-block" data-testid="footer-logo">
                <span className="text-3xl font-heading font-bold text-white tracking-tight">
                  RAINDAS
                </span>
              </Link>
              <p className="mt-6 text-slate-400 text-base leading-relaxed max-w-sm">
                Engineering intelligent systems for modern organizations. Software products, digital systems, custom software engineering.
              </p>
              
              {/* Newsletter */}
              <form onSubmit={handleNewsletterSubmit} className="mt-8">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-4">
                  Subscribe to updates
                </p>
                <div className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-slate-600 px-4 py-3 focus:border-mint focus:outline-none transition-colors"
                    data-testid="footer-newsletter-input"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-mint text-black px-4 py-3 hover:bg-coral transition-colors disabled:opacity-50"
                    data-testid="footer-newsletter-btn"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>

            {/* Links */}
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-6">
                  Company
                </h4>
                <ul className="space-y-4">
                  {footerLinks.company.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-slate-400 hover:text-mint text-sm transition-colors"
                        data-testid={`footer-link-${link.name.toLowerCase()}`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-6">
                  Solutions
                </h4>
                <ul className="space-y-4">
                  {footerLinks.solutions.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-slate-400 hover:text-mint text-sm transition-colors"
                        data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-6">
                  Contact
                </h4>
                <ul className="space-y-4">
                  <li>
                    <a 
                      href="mailto:hello@raindas.co"
                      className="text-slate-400 hover:text-mint text-sm transition-colors"
                      data-testid="footer-email-finance"
                    >
                      hello@raindas.co
                    </a>
                  </li>
                  <li>
                    <a 
                      href="mailto:studio@raindas.co"
                      className="text-slate-400 hover:text-mint text-sm transition-colors"
                      data-testid="footer-email-compliance"
                    >
                      studio@raindas.co
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-600 text-sm">
              © {new Date().getFullYear()} Raindas Technologies Limited
            </p>
            <p className="text-slate-700 text-xs font-mono">
              raindas.co
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
